/**
 * Is a game being played right now, and a clock to keep asking.
 *
 * Nothing stored says a game is underway: the status enum is
 * scheduled/final/forfeit/canceled and a game carries no `updatedAt`, so the
 * only facts available are the scheduled start and the score once somebody
 * posts it. Liveness is therefore derived from the clock, with the trade-off
 * that a rain-delayed game reads live until its window elapses and a game
 * running long goes quiet while it is still being played (spec §3.3).
 */
import { useEffect, useState } from "react";
import type { EngineConfig } from "@alpha/engine";
import type { Game } from "./types";

/**
 * True while `now` falls inside the game's scheduled window.
 *
 * The window is `[scheduledAt, scheduledAt + gameLengthMinutes)` — half-open,
 * and deliberately the same arithmetic `packages/engine/src/conflicts.ts` uses
 * to decide that two games double-book a field, so the two cannot disagree
 * about how long a game lasts.
 *
 * Only the arithmetic is shared, and the status guards are meant to differ:
 * `conflicts.ts` skips only `canceled`, because a game somebody has scored
 * still occupied its field and still owes its teams rest. This one skips
 * everything but `scheduled`, because a scored game is not being played. Do
 * not "fix" that divergence by syncing the two filters.
 *
 * `now` is a parameter and is never read from the clock here, which is what
 * lets every case be asserted at a fixed instant.
 *
 * Takes the config rather than the whole tournament: an earlier draft also
 * refused to light up a `complete` or `archived` tournament, which buys nothing
 * — every window on a finished event ended in the past, so the clock already
 * excludes it — and could only fire by killing the badge for a live event whose
 * director pressed "Mark complete" early (spec §3.2.1).
 */
export function isGameLive(game: Game, config: EngineConfig, now: number): boolean {
  if (game.status !== "scheduled") return false;
  if (!game.scheduledAt) return false;
  const start = Date.parse(game.scheduledAt);
  if (Number.isNaN(start)) return false;
  return start <= now && now < start + config.gameLengthMinutes * 60_000;
}

/**
 * A timestamp that advances on its own, so a clock-derived badge appears and
 * clears without waiting on the network.
 *
 * Re-rendering on data arrival cannot do this job. A refetch that returns an
 * unchanged bundle carries no news about the time, and the bundle is unchanged
 * for exactly the stretch a game is in progress; `useBundle` stops polling an
 * archived tournament altogether; and poll timing has no relationship to game
 * boundaries anyway. Boundary precision is ±`intervalMs` — 30s mirrors the
 * bundle poll, so these pages keep the tick rate they already have.
 *
 * Call it beside `useBundle` at the top of a route, above the early returns for
 * loading and error: a hook down where the derived values live would be
 * conditional. One interval per page with `now` passed down, never one per card
 * — a Saturday at a four-field park renders twenty-plus of them.
 */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
